import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { BaseComponent } from '../base-component'
import EmblaCarousel, {
  EmblaCarouselType,
  EmblaPluginType,
} from 'embla-carousel'
import ClassNamesPlugin from 'embla-carousel-class-names'
import { getFocusTargets, $el, $list } from '../../utils/utils'
import { isLeftKey, isRightKey } from '../../utils/key'
import { oneAtTimeAutoPlay, seamlessAutoPlay } from './carousel-autoplay'
import { isMobile } from '../../utils/check-media'
import { OptionsType } from 'embla-carousel/components/Options'
import { AlignmentOptionType } from 'embla-carousel/components/Alignment'
import { ScrollContainOptionType } from 'embla-carousel/components/ScrollContain'
import { AxisOptionType } from 'embla-carousel/components/Axis'

export class CarouselComponent extends BaseComponent {
  withSimpleAutoplay: boolean
  withSeamlessAutoplay: boolean
  withAutoplay: boolean
  isTouching: boolean
  isPlay: boolean
  lastIsMobile: boolean
  autoplay: any
  embla: EmblaCarouselType
  resizeObserver: ResizeObserver

  constructor() {
    super()
    this.withSimpleAutoplay = this.dataset.autoplayMode === 'one_at_time'
    this.withSeamlessAutoplay = this.dataset.autoplayMode === 'seamless'
    this.withAutoplay = this.withSimpleAutoplay || this.withSeamlessAutoplay
  }

  mountComponent(): void {
    this.setCarousel()
    this.setAutoplay()

    this.addListener(this, 'keydown', this.handleKeydown)

    this.embla.on('select', this.handleCarouselSelect)
    this.embla.on('pointerUp', this.handleCarouselPointerUp)
    this.embla.on('pointerDown', this.handleCarouselPointerDown)

    const withHoverPause = this.hasAttribute('with-hover-pause') && this.isPlay
    if (withHoverPause) {
      this.addListener(this, 'mouseenter', this.handleCarouselEnter)
      this.addListener(this, 'mouseleave', this.handleCarouselLeave)
    }

    if (this.isEditor) {
      this.resizeObserver = new ResizeObserver(this.handleCarouselResize)
      this.resizeObserver.observe(this)
      this.editor.on('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.on('SECTION_LOAD', this.handleSectionLoad)
    }
  }

  unmountComponent(): void {
    this.embla.destroy()

    if (this.isEditor) {
      this.resizeObserver.disconnect()
    }
  }

  handleKeydown = (event: KeyboardEvent): void => {
    event.preventDefault()

    if (event.keyCode === 9) {
      const targets = $list(getFocusTargets(), this)
      const index = targets.findIndex(target => target === event.target)
      const target = targets[index]
      const nextTarget = targets[index + 1]
      const prevTarget = targets[index - 1]
      const activeElement = document.activeElement as HTMLElement

      if (activeElement === target) {
        this.focusScroll(target)
      }

      if (event.shiftKey) {
        if (prevTarget) {
          this.focusScroll(prevTarget)
        } else {
          activeElement.blur()
        }
      } else {
        if (nextTarget) {
          this.focusScroll(nextTarget)
        } else {
          activeElement.blur()
        }
      }
    }

    if (isLeftKey(event)) {
      this.embla.scrollPrev()
    }

    if (isRightKey(event)) {
      this.embla.scrollNext()
    }
  }

  handleCarouselResize = (): void => {
    if (this.lastIsMobile !== isMobile() && !this.isPlay) {
      this.play()
    }

    this.lastIsMobile = isMobile()
  }

  focusScroll = (target: HTMLElement): void => {
    const slides = [...this.embla.slideNodes()]
    const slideIndex = slides.findIndex(slide => slide.contains(target))

    if (slideIndex !== -1) {
      this.embla.scrollTo(slideIndex, true)
      this.stop()
    }

    target.focus()
  }

  handleCarouselEnter = (): void => {
    if (isMobile()) {
      return
    }

    this.autoplay?.pause()
  }

  handleCarouselSelect = (): void => {
    if (this.isTouching) {
      this.stop()
    }

    this.emit('carouselSelect', {})
  }

  handleCarouselPointerUp = (): void => {
    this.isTouching = false
  }

  handleCarouselPointerDown = (): void => {
    this.isTouching = true
  }

  handleCarouselLeave = (): void => {
    if (isMobile()) {
      return
    }

    this.autoplay?.play()
  }

  handleBlockSelect = ({ detail: { sectionId, blockId, load } }): void => {
    if (this.dataset.sectionId !== sectionId) {
      return
    }

    const slides = this.embla.slideNodes()
    const slideIndex = slides.findIndex(
      ({ attributes }) => attributes['block-id']?.value === blockId,
    )

    if (slideIndex !== -1) {
      this.embla.scrollTo(slideIndex, load)
      this.stop()
    }
  }

  handleSectionLoad = ({ detail: { sectionId } }): void => {
    if (this.dataset.sectionId === sectionId) {
      this.stop()
      this.reInit()

      if (this.withAutoplay) {
        this.setAutoplay()
        this.play()
      }
    }
  }

  getOptions = (): Partial<OptionsType> => ({
    loop: this.hasAttribute('data-loop'),
    dragFree: this.hasAttribute('data-drag-free'),
    watchDrag: this.hasAttribute('data-draggable'),
    skipSnaps: this.hasAttribute('data-skip-snaps'),
    align: this.getAttribute('data-align') as AlignmentOptionType,
    containScroll: this.getAttribute(
      'data-contain-scroll',
    ) as ScrollContainOptionType,
    inViewThreshold: +(this.dataset.inViewThreshhold || 0) || 0.75,
    axis: this.getAttribute('data-axis') as AxisOptionType,
    breakpoints: this.hasAttribute('data-breakpoints')
      ? JSON.parse(this.dataset.breakpoints)
      : undefined,
    slidesToScroll: +(this.getAttribute('data-slides-to-scroll') ?? 1),
  })

  getPlugins = (): EmblaPluginType[] => {
    const plugins: EmblaPluginType[] = [ClassNamesPlugin()]

    if (this.hasAttribute('data-with-wheel-gestures')) {
      plugins.push(
        WheelGesturesPlugin({
          forceWheelAxis: this.getAttribute('data-axis') as any,
        }),
      )
    }

    return plugins
  }

  setCarousel(): void {
    const viewport = $el('[data-carousel-viewport]', this)

    if (!viewport) {
      return
    }

    this.embla = EmblaCarousel(viewport, this.getOptions(), this.getPlugins())
  }

  setAutoplay() {
    const autoplayInterval = +(this.dataset.autoplayInterval || 0)

    this.isPlay = autoplayInterval > 0

    if (this.withSimpleAutoplay) {
      this.autoplay = oneAtTimeAutoPlay(this.embla, autoplayInterval * 1000)
      this.autoplay.start()
    }

    if (this.withSeamlessAutoplay) {
      this.autoplay = seamlessAutoPlay(this.embla, autoplayInterval)
      this.autoplay.play()
    }
  }

  stop(): void {
    if (!this.withAutoplay) {
      return
    }

    this.autoplay.stop()

    this.isPlay = false

    this.emit('changePlayState', {})
  }

  play(): void {
    if (!this.withAutoplay) {
      return
    }

    this.autoplay.start()
    this.autoplay.play()

    this.isPlay = true

    this.emit('changePlayState', {})
  }

  reInit = (options: Partial<OptionsType> = {}): void => {
    this.embla.reInit({ ...this.getOptions(), ...options }, this.getPlugins())
  }
}
